<template>
    <div class="flex flex-col gap-1">
        <BaseTextarea
            :id="id"
            v-model:input="localValue"
            :maxlength="maxlength"
            :placeholder="placeholder"
            :aria-label="ariaLabel"
            rows="4"
            class="bg-control-bg border border-control-border rounded-lg p-3 body-small text-control-text focus-field"
            @blur="saveIfChanged" />
        <div class="flex justify-between details text-ink-muted">
            <Transition name="fade">
                <span v-if="isShowing">{{ t("settings.account.customInstructions.saved") }}</span>
            </Transition>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- The slash is pure punctuation between two counts -->
            <output :for="id" :aria-label="t('settings.account.customInstructions.characterCountA11yLabel')">{{ characterCount }}/{{ maxlength }}</output>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        id: string;
        ariaLabel: string;
        modelValue?: string | null;
        maxlength?: number;
        placeholder?: string;
    }>(),
    {
        maxlength: 1500,
        placeholder: "",
    },
);

const emit = defineEmits<{ save: [value: string] }>();

const localValue = ref(props.modelValue ?? "");
const lastSavedValue = ref(props.modelValue ?? "");
const { isShowing, show: showStatus } = useTimedShow({ duration: 2000 });

watch(
    () => props.modelValue,
    (newVal) => {
        const normalized = newVal ?? "";
        localValue.value = normalized;
        lastSavedValue.value = normalized;
    },
);

const characterCount = computed(() => localValue.value.length);

function saveIfChanged() {
    if (localValue.value === lastSavedValue.value) {
        return;
    }

    emit("save", localValue.value);
    lastSavedValue.value = localValue.value;
    showStatus();
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
